import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import Pagination from "../components/Pagination";
import { useCompare } from "../context/CompareContext";
import { useMessages } from "../context/MessagesContext";
import * as api from "../api";

const ITEMS_PER_PAGE = 12;

const categories = [
  { id: "tous",         label: "Tous" },
  { id: "yachting",    label: "Le Yachting" },
  { id: "moteur",      label: "Les Bateaux à Moteur" },
  { id: "voile",       label: "La Voile" },
  { id: "equipement",  label: "L'Équipement" },
  { id: "sports",      label: "Les Sports et Loisirs" },
  { id: "marinas",     label: "Les Marinas" },
  { id: "navires",     label: "Navires Professionnels" },
  { id: "construction",label: "La Construction Navale" },
];

// Sous-catégories par catégorie parent
const sousCategoriesMap = {
  yachting:     [{ slug: "yachts-luxe", label: "Yachts de luxe" }, { slug: "yacht-toys", label: "Yacht toys" }],
  moteur:       [{ slug: "moteur-cabine", label: "Avec cabine" }, { slug: "moteur-open", label: "Open" }, { slug: "pneumatiques", label: "Pneumatiques" }, { slug: "offshore-jetski", label: "Offshore / Jet-ski" }, { slug: "electrique", label: "Électrique & hybride" }],
  voile:        [{ slug: "monocoque", label: "Monocoques" }, { slug: "multicoque", label: "Multicoques" }, { slug: "voile-legere", label: "Voile légère" }, { slug: "equipement-voile", label: "Équipements voile" }],
  equipement:   [{ slug: "greement", label: "Gréements" }, { slug: "electronique", label: "Électronique" }, { slug: "moteurs", label: "Moteurs" }, { slug: "propulsion", label: "Propulsion" }, { slug: "pont", label: "Pont & entretien" }, { slug: "securite", label: "Sécurité" }, { slug: "vetements", label: "Vêtements" }, { slug: "confort", label: "Confort à bord" }, { slug: "eau-electricite", label: "Eau & électricité" }],
  sports:       [{ slug: "kayak", label: "Kayaks, canoës" }, { slug: "plongee", label: "Plongée" }, { slug: "chasse-marine", label: "Chasse sous-marine" }, { slug: "peche", label: "Pêche" }, { slug: "glisse", label: "Sports de glisse" }, { slug: "loisirs", label: "Bases de loisirs" }],
  marinas:      [{ slug: "manutention", label: "Manutention" }, { slug: "amenagement", label: "Aménagement" }, { slug: "flottant", label: "Maisons flottantes" }],
  navires:      [{ slug: "transport", label: "Transport" }, { slug: "services", label: "Services" }, { slug: "travail", label: "Travail" }, { slug: "tourisme", label: "Tourisme" }],
  construction: [{ slug: "materiaux", label: "Matériaux" }, { slug: "outillage", label: "Outillage" }],
};

const categoryMap = {
  "Bateaux à Moteur": "moteur", "Yachts de Luxe": "yachting", "Équipement": "equipement",
  "Sports et Loisirs": "sports", "Voiliers": "voile",
  yachting: "yachting", moteur: "moteur", voile: "voile", equipement: "equipement",
  sports: "sports", marinas: "marinas", navires: "navires", construction: "construction",
};

function Catalogue() {
  const [searchParams, setSearchParams] = useSearchParams();

  const { user } = useAuth();
  const { toggleFavori, isFavori } = useMessages();
  const { addToCompare, removeFromCompare, isInCompare } = useCompare();

  const [activeCategory, setActiveCategory]         = useState(categoryMap[searchParams.get("categorie")] || "tous");
  const [activeSousCategorie, setActiveSousCategorie] = useState(searchParams.get("sous_categorie") || "tous");
  const [searchTerm, setSearchTerm]                 = useState("");
  const [viewMode, setViewMode]                     = useState("grid");
  const [selectedState, setSelectedState]           = useState("tous");
  const [sortBy, setSortBy]                         = useState("recent");
  const [page, setPage]                             = useState(1);

  // ✅ Synchroniser le state quand l'URL change (ex: clic footer)
  useEffect(() => {
    const cat = categoryMap[searchParams.get("categorie")] || "tous";
    const sousCat = searchParams.get("sous_categorie") || "tous";
    setActiveCategory(cat);
    setActiveSousCategorie(sousCat);
    setPage(1);
  }, [searchParams]);

  // ✅ Server-side pagination
  const [annonces, setAnnonces]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]     = useState(false);

  const fetchAnnonces = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: ITEMS_PER_PAGE };
      if (activeCategory !== "tous") params.categorie = activeCategory;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const res = await api.getAnnonces(params);
      let data = res.data.annonces || [];
      // Fix is_new field name
      data = data.map(a => ({ ...a, isNew: a.isNew || a.is_new }));
      // Client-side filters (sous_categorie, état) que le backend ne gère pas
      if (activeSousCategorie !== "tous") data = data.filter(a => a.sous_categorie === activeSousCategorie);
      if (selectedState === "neuf")     data = data.filter(a => a.isNew);
      if (selectedState === "occasion") data = data.filter(a => !a.isNew);
      // Sort
      if (sortBy === "name_asc")  data = [...data].sort((a,b) => a.nom.localeCompare(b.nom));
      if (sortBy === "name_desc") data = [...data].sort((a,b) => b.nom.localeCompare(a.nom));
      setAnnonces(data);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error("Erreur catalogue:", err);
    } finally {
      setLoading(false);
    }
  }, [page, activeCategory, activeSousCategorie, searchTerm, selectedState, sortBy]);

  useEffect(() => { fetchAnnonces(); }, [fetchAnnonces]);

  const handleCategoryClick = (catId) => {
    setActiveCategory(catId);
    setActiveSousCategorie("tous");
    setPage(1);
  };

  const handleSousCategorieClick = (slug) => {
    setActiveSousCategorie(slug === activeSousCategorie ? "tous" : slug);
    setPage(1);
  };

  const paginatedData = annonces; // Already paginated server-side


  const resetFilters = () => {
    setSelectedState("tous"); setSortBy("recent");
    setActiveCategory("tous"); setActiveSousCategorie("tous");
    setSearchTerm(""); setPage(1);
  };

  const handleToggleFavori = (e, productId) => {
    e.preventDefault();
    if (!user) { alert("Connectez-vous pour ajouter aux favoris"); return; }
    toggleFavori(productId);
  };

  const sousCats = activeCategory !== "tous" ? (sousCategoriesMap[activeCategory] || []) : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Navbar />

      <div className="page-banner" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1920')", backgroundSize: "cover", backgroundPosition: "center", textAlign: "center", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.3)" }} />
        <div style={{ position: "relative" }}>
          <h1 style={{ fontSize: "42px", fontWeight: "700", color: "white", marginBottom: "12px" }}>Catalogue MarinaDZ</h1>
          <p style={{ fontSize: "16px", color: "white", opacity: 0.9 }}>Explorez notre sélection de produits et services maritimes</p>
        </div>
      </div>

      <div className="catalogue-main" style={{ flex: 1, backgroundColor: "#f9fafb" }}>
        <div className="catalogue-inner">

          {/* Sidebar */}
          <aside className="catalogue-sidebar">
            <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", marginBottom: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                <span style={{ fontWeight: "600", fontSize: "16px", color: "#1f2937" }}>Filtres</span>
                <button onClick={resetFilters} style={{ fontSize: "12px", color: "#0d9488", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Réinitialiser</button>
              </div>

              <div style={{ display: "flex", alignItems: "center", backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "0 12px", marginBottom: "8px" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input type="text" placeholder="Rechercher..." value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                  style={{ flex: 1, padding: "10px", border: "none", outline: "none", fontSize: "14px", backgroundColor: "transparent" }} />
              </div>

              {/* Suggestions IA */}
              {/* Catégories */}
              <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", marginBottom: "15px" }}>Catégories</h4>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px" }}>
                {categories.map(cat => (
                  <li key={cat.id}>
                    <button onClick={() => handleCategoryClick(cat.id)}
                      style={{ width: "100%", textAlign: "left", padding: "10px 15px", marginBottom: "5px", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: activeCategory === cat.id ? "600" : "400", backgroundColor: activeCategory === cat.id ? "#0d9488" : "transparent", color: activeCategory === cat.id ? "white" : "#4b5563" }}>
                      {cat.label}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Sous-catégories — affichées seulement si une catégorie est active */}
              {sousCats.length > 0 && (
                <>
                  <h4 style={{ fontSize: "13px", fontWeight: "600", color: "#374151", marginBottom: "10px", paddingTop: "12px", borderTop: "1px solid #e5e7eb" }}>
                    Sous-catégories
                  </h4>
                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px" }}>
                    <li>
                      <button onClick={() => setActiveSousCategorie("tous")}
                        style={{ width: "100%", textAlign: "left", padding: "8px 12px", marginBottom: "3px", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: activeSousCategorie === "tous" ? "600" : "400", backgroundColor: activeSousCategorie === "tous" ? "#f0fdfa" : "transparent", color: activeSousCategorie === "tous" ? "#0d9488" : "#6b7280" }}>
                        Toutes
                      </button>
                    </li>
                    {sousCats.map(sc => (
                      <li key={sc.slug}>
                        <button onClick={() => handleSousCategorieClick(sc.slug)}
                          style={{ width: "100%", textAlign: "left", padding: "8px 12px", marginBottom: "3px", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: activeSousCategorie === sc.slug ? "600" : "400", backgroundColor: activeSousCategorie === sc.slug ? "#f0fdfa" : "transparent", color: activeSousCategorie === sc.slug ? "#0d9488" : "#6b7280" }}>
                          {sc.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* État */}
              <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", marginBottom: "15px", paddingTop: "15px", borderTop: "1px solid #e5e7eb" }}>État</h4>
              <div style={{ marginBottom: "20px" }}>
                {["tous", "neuf", "occasion"].map(state => (
                  <label key={state} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0", cursor: "pointer", fontSize: "14px", color: "#4b5563" }}>
                    <input type="radio" name="state" checked={selectedState === state} onChange={() => { setSelectedState(state); setPage(1); }} style={{ accentColor: "#0d9488" }} />
                    {state.charAt(0).toUpperCase() + state.slice(1)}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ backgroundColor: "#f0fdfa", borderRadius: "12px", padding: "20px", border: "2px solid #0d9488" }}>
              <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#0d9488", marginBottom: "10px" }}>💼 Devenez Exposant</h4>
              <p style={{ fontSize: "12px", color: "#065f46", marginBottom: "15px", lineHeight: "1.5" }}>Publiez vos annonces et touchez des milliers d'acheteurs potentiels</p>
              <Link to="/inscription" style={{ display: "block", textAlign: "center", padding: "10px", backgroundColor: "#0d9488", color: "white", borderRadius: "6px", textDecoration: "none", fontSize: "13px", fontWeight: "600" }}>En savoir plus</Link>
            </div>
          </aside>

          <section className="catalogue-content">
            {/* Fil d'Ariane sous-catégorie */}
            {activeSousCategorie !== "tous" && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", fontSize: "13px", color: "#6b7280" }}>
                <button onClick={() => setActiveSousCategorie("tous")} style={{ background: "none", border: "none", color: "#0d9488", cursor: "pointer", fontSize: "13px", padding: 0 }}>
                  {categories.find(c => c.id === activeCategory)?.label}
                </button>
                <span>›</span>
                <span style={{ fontWeight: "600", color: "#1f2937" }}>
                  {sousCats.find(s => s.slug === activeSousCategorie)?.label}
                </span>
                <button onClick={() => setActiveSousCategorie("tous")} style={{ marginLeft: "8px", background: "#f3f4f6", border: "none", borderRadius: "50%", width: "20px", height: "20px", cursor: "pointer", fontSize: "12px", color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px", flexWrap: "wrap", gap: "15px" }}>
              <p style={{ color: "#6b7280", fontSize: "14px" }}>
                <strong style={{ color: "#1f2937" }}>{total}</strong> annonce{total > 1 ? "s" : ""} trouvée{total > 1 ? "s" : ""}
              </p>
              <div style={{ display: "flex", gap: "15px", alignItems: "center", flexWrap: "wrap" }}>
                <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
                  style={{ padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: "6px", fontSize: "14px", outline: "none", backgroundColor: "white", cursor: "pointer" }}>
                  <option value="recent">Plus récent</option>
                  <option value="name_asc">Nom A-Z</option>
                  <option value="name_desc">Nom Z-A</option>
                </select>
                <div style={{ display: "flex", gap: "5px" }}>
                  <button onClick={() => setViewMode("grid")} style={{ padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: "6px", cursor: "pointer", backgroundColor: viewMode === "grid" ? "#0d9488" : "white", color: viewMode === "grid" ? "white" : "#374151" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                  </button>
                  <button onClick={() => setViewMode("list")} style={{ padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: "6px", cursor: "pointer", backgroundColor: viewMode === "list" ? "#0d9488" : "white", color: viewMode === "list" ? "white" : "#374151" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: "60px", backgroundColor: "white", borderRadius: "12px" }}>
                <div style={{ width: "40px", height: "40px", border: "3px solid #e5e7eb", borderTop: "3px solid #0d9488", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
                <p style={{ color: "#6b7280" }}>Chargement des annonces...</p>
              </div>
            ) : paginatedData.length > 0 ? (
              <>
                <div className="grid-responsive" style={{ display: "grid", gridTemplateColumns: viewMode === "grid" ? "repeat(2, 1fr)" : "1fr", gap: "25px" }}>
                  {paginatedData.map(product => (
                    <Link to={`/annonce/${product.id}`} key={product.id}
                      style={{ textDecoration: "none", backgroundColor: "white", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", transition: "transform 0.2s, box-shadow 0.2s" }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)"; }}>
                      <div style={{ position: "relative" }}>
                        <img src={product.images?.[0] || product.image || "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=600"}
                          alt={product.nom} style={{ width: "100%", height: "180px", objectFit: "cover" }}
                          onError={e => { e.target.src = "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=600"; }} />
                        {product.isNew && (
                          <span style={{ position: "absolute", top: "12px", left: "12px", backgroundColor: "#0d9488", color: "white", padding: "5px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: "600" }}>Nouveau</span>
                        )}
                        <button onClick={e => handleToggleFavori(e, product.id)}
                          style={{ position: "absolute", top: "12px", right: "12px", backgroundColor: "white", border: "none", borderRadius: "50%", width: "36px", height: "36px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill={user && isFavori(product.id) ? "#0d9488" : "none"} stroke={user && isFavori(product.id) ? "#0d9488" : "#374151"} strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                          </svg>
                        </button>
                      </div>
                      <div style={{ padding: "20px" }}>
                        <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#1f2937", marginBottom: "6px" }}>{product.nom}</h3>
                        <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "10px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{product.description}</p>
                        {product.prix && <p style={{ fontSize: "15px", fontWeight: "700", color: "#0d9488", marginBottom: "10px" }}>{product.prix}</p>}
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          {product.localisation && (
                            <span style={{ fontSize: "12px", color: "#6b7280", display: "flex", alignItems: "center", gap: "3px" }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><circle cx="12" cy="11" r="3"/></svg>
                              {product.localisation}
                            </span>
                          )}
                          {product.marque && (
                            <span style={{ fontSize: "12px", color: "#6b7280", backgroundColor: "#f3f4f6", padding: "3px 8px", borderRadius: "4px" }}>{product.marque}</span>
                          )}
                        </div>
                        <button onClick={e => { e.preventDefault(); isInCompare(product.id) ? removeFromCompare(product.id) : addToCompare(product); }}
                          style={{ marginTop: "10px", width: "100%", padding: "7px", border: `1px solid ${isInCompare(product.id) ? "#0d9488" : "#e5e7eb"}`, borderRadius: "6px", backgroundColor: isInCompare(product.id) ? "#f0fdfa" : "white", color: isInCompare(product.id) ? "#0d9488" : "#6b7280", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                          {isInCompare(product.id) ? "Comparé ✓" : "Comparer"}
                        </button>
                      </div>
                    </Link>
                  ))}
                </div>
                <Pagination current={page} total={total} perPage={ITEMS_PER_PAGE}
                  onChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "80px 20px", backgroundColor: "white", borderRadius: "12px" }}>
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1" style={{ margin: "0 auto 20px", display: "block" }}>
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <h3 style={{ fontSize: "18px", color: "#1f2937", marginBottom: "8px" }}>Aucune annonce trouvée</h3>
                <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "20px" }}>
                  {annonces.length === 0 ? "Aucune annonce publiée pour l'instant." : "Aucun résultat pour ces filtres."}
                </p>
                {annonces.length > 0 && (
                  <button onClick={resetFilters} style={{ padding: "10px 20px", backgroundColor: "#0d9488", color: "white", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}>
                    Réinitialiser les filtres
                  </button>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Footer />
    </div>
  );
}

export default Catalogue;